import React, { useEffect, useState } from "react";
import { useStripe, useElements } from "@stripe/react-stripe-js";
import {
    CardNumberElement,
    CardExpiryElement,
    CardCvcElement
} from "@stripe/react-stripe-js";
import { ConsoleLogger } from "aws-amplify/utils";
import {
    graphql,
    graphqlOperation,
    postApi
} from "../../modules/AmplifyServices";
import {
    Grid,
    Button,
    Typography,
    FormControlLabel,
    Snackbar,
    DialogActions,
    Radio,
    TextField
} from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import "./stripe.css";
import { updateClient } from "../../graphql/mutations";
import {
    getStripeAmount,
    getCurrencySymbol,
    deleteHeldSlots
} from "../../modules/AdhocBookingService";
import { useAtom } from "jotai";
import {
    bookingDetailsAtom,
    chargeAtom,
    stripeChargeCardStateAtom
} from "../../atoms/booking";
import { blue } from "@mui/material/colors";
import CircularProgress from "@mui/material/CircularProgress";
import CheckIcon from "@mui/icons-material/Check";
import { companyAtom, snackBarAtom } from "../../atoms/atoms";
import { MBX_ERRORS, PAY_TYPE_NEW_CC } from "../../utils/Constants";
import StripeInput from "./StripeInput";
import { colors } from "../../utils/Constants";
import VISA_LOGO from "../../images/visa_blue.png";
import MC_LOGO from "../../images/mc_main.png";
import AMEX_LOGO from "../../images/amex_main.png";
import DISCOVER_LOGO from "../../images/discover_main.png";
class MbxStripeError extends Error {
    constructor(message) {
        super(message);
        this.name = MBX_ERRORS.stripeError;
    }
}
const logger = new ConsoleLogger("StripeChargeCard");

const StripeChargeCard = ({
    setProcessingPayment,
    onProcessPayment,
    setElements,
    disabled
}) => {
    const { MB_GREY_LIGHT } = colors;
    const stripe = useStripe();
    const elements = useElements();
    const [state, setState] = useState({
        useDifferentCard: false,
        errorMessage: "",
        thanksMessage: false,
        cardForm: false,
        msgOpen: false,
        snackMsg: "",
        bookingDetails: {},
        showSlotsTimeoutExpired: false,
        packageCreditsAreNotValid: false
    });
    const [stripeChargeCardState, setStripeChargeCardState] = useAtom(
        stripeChargeCardStateAtom
    );
    const [payClicked, setPayClicked] = useState(false);
    const [{ client, cardExpired, currency, heldSlots, payType }] =
        useAtom(bookingDetailsAtom);
    const [company] = useAtom(companyAtom);
    const [, setSnackBar] = useAtom(snackBarAtom);
    const [cbu] = useAtom(chargeAtom);

    useEffect(() => {
        setElements(elements);
    }, [elements]);

    const createOptions = () => {
        return {
            style: {
                base: {
                    fontSize: "16px",
                    color: "#424770",
                    letterSpacing: "0.025em",
                    "::placeholder": {
                        color: "#aab7c4"
                    }
                },
                invalid: {
                    color: "#c23d4b"
                }
            }
        };
    };

    const buttonProgress = () => {
        return {
            style: {
                buttonProgress: {
                    color: blue[500],
                    position: "absolute",
                    top: "10%",
                    marginTop: -1,
                    marginLeft: -1
                }
            }
        };
    };

    const wrapper = () => {
        return {
            style: {
                wrapper: {
                    margin: "8px",
                    position: "relative"
                }
            }
        };
    };

    async function handleClick() {
        setPayClicked(true);
        setProcessingPayment();

        if (!client.stripeCustomerId || state.useDifferentCard || cardExpired) {
            //create token, customer
            const cne = elements.getElement(CardNumberElement);
            const connectedAccount =
                company.stripeConnectEnabled && company.stripeAccount
                    ? { stripeAccount: company.stripeAccount }
                    : null;
            let { token, error } = await stripe.createToken(
                cne,
                connectedAccount
            );
            if (error) {
                setPayClicked(false);
                setState({
                    ...state,
                    msgOpen: true,
                    snackMsg: error.message
                });

                return;
            }
            try {
                const resultStripe = await postApi(
                    "stripesavecard",
                    "/savecardinfo",
                    {
                        body: {
                            token,
                            emailaddress: client.user.emailaddress,
                            companyId: company.id,
                            stripeAccount: company.stripeAccount,
                            stripeConnectEnabled: company.stripeConnectEnabled
                        }
                    }
                );
                if (resultStripe.error) {
                    setPayClicked(false);
                    setState({
                        ...state,
                        msgOpen: true,
                        snackMsg: resultStripe.error.message
                    });

                    return;
                }
                if (!resultStripe.customer) {
                    setPayClicked(false);
                    setState({
                        ...state,
                        msgOpen: true,
                        snackMsg: "Could not save credit card information."
                    });

                    return;
                }
                // save customer id from result
                const stripeCustomerId = resultStripe.customer.id;
                const cclast4 = resultStripe.customer.sources.data[0].last4;
                const input = {
                    id: client.id,
                    defaultpartialcc: cclast4,
                    stripeCustomerId
                };
                // save customer.id from Stripe to Customer type
                const updatedClient = await graphql(
                    graphqlOperation(updateClient, { input })
                );
                await processPayment(updatedClient.data.updateClient);
            } catch (e) {
                console.log("error", JSON.stringify(e));
                setPayClicked(false);
                setState({
                    ...state,
                    msgOpen: true,
                    snackMsg: "Error while saving credit card information."
                });

                return;
            }
        } else await processPayment(client);
    }

    async function chargeStripe(stripeCustomerId) {
        const resultStripeCharge = await postApi(
            "stripechargecard",
            "/stripechargecard",
            {
                body: {
                    receiptemailaddress: client.user.emailaddress,
                    stripecustomerid: stripeCustomerId,
                    customerCurrency: currency,
                    amount: cbu.total ? getStripeAmount(cbu.total) : 0,
                    credit: false,
                    prevcharge: false,
                    companyId: company.id,
                    stripeAccount: company.stripeAccount,
                    stripeConnectEnabled: company.stripeConnectEnabled
                }
            }
        );
        return resultStripeCharge;
    }

    async function processPayment(client) {
        try {
            setSnackBar({
                open: true,
                snackMsg: "Sending confirmation email"
            });

            //Charge customer
            const resultStripeCharge = await chargeStripe(
                client.stripeCustomerId
            );
            if (resultStripeCharge && resultStripeCharge.error) {
                try {
                    await deleteHeldSlots(heldSlots);
                } catch (e) {
                    console.log("error while deleting held slots", e);
                }
                return new MbxStripeError(
                    resultStripeCharge.error.raw?.message
                );
            }
            if (
                (resultStripeCharge && !resultStripeCharge.charge) ||
                (resultStripeCharge &&
                    resultStripeCharge.charge &&
                    resultStripeCharge.charge.paid !== true)
            ) {
                try {
                    await deleteHeldSlots(heldSlots);
                } catch (e) {
                    console.log("error while deleting held slots", e);
                }
                return new MbxStripeError("Unable to process payment");
            }
            return onProcessPayment(resultStripeCharge);
        } catch (error) {
            setPayClicked(false);
            setState({
                ...state,
                msgOpen: true,
                snackMsg: "Unable to process your payment."
            });
            console.error(
                "An error occurred processing the payment. The error was: ",
                error
            );
        }
    }
    const ccyFormat = (num) => {
        return `${Number.parseFloat(num ? num : 0).toFixed(2)}`;
    };
    const handleChange = ({ error, ...rest }) => {
        if (error) {
            setState({ ...state, errorMessage: error.message });
            setStripeChargeCardState({
                ...stripeChargeCardState,
                [rest.elementType]: {
                    brand: rest.brand || "unknown",
                    complete: rest.complete,
                    elementType: rest.elementType,
                    empty: rest.empty,
                    value: rest.value,
                    error: error.message
                }
            });
        } else {
            setState({ ...state, errorMessage: "" });
            setStripeChargeCardState({
                ...stripeChargeCardState,
                [rest.elementType]: {
                    brand: rest.brand || "unknown",
                    complete: rest.complete,
                    elementType: rest.elementType,
                    empty: rest.empty,
                    value: rest.value,
                    error: ""
                }
            });
        }
    };
    const handleCardChange = (name) => (event) => {
        setState({ ...state, [name]: event.target.checked });
    };

    const handleMsgClose = (event, reason) => {
        setState({ ...state, msgOpen: false });
    };

    let hasStripeCustomerId = false;
    let hasCardExpired;
    try {
        hasStripeCustomerId = !!client.stripeCustomerId;
        hasCardExpired = !!cardExpired;
    } catch (err) {
        logger.error(
            "Error attempting to determine stripeCustomerId. Error was: " +
                JSON.stringify(err)
        );
    }

    return (
        <>
            <FormControlLabel
                value={PAY_TYPE_NEW_CC}
                control={
                    <Radio
                        sx={{
                            color: MB_GREY_LIGHT
                        }}
                        disabled={disabled}
                        size="small"
                    />
                }
                label={
                    <Grid container alignItems="center" flexDirection="row">
                        <Grid item xs={8}>
                            <Typography>{`Pay with a new card`}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <LogoContainer />
                        </Grid>
                    </Grid>
                }
                sx={{
                    "&.MuiFormControlLabel-root": {
                        marginRight: "0px"
                    }
                }}
            />
            {payType === PAY_TYPE_NEW_CC && (
                <Grid container item xs={12} spacing={2.25}>
                    <Grid item xs={12}>
                        <TextField
                            label="Credit card number"
                            name="ccnumber"
                            variant="outlined"
                            sx={{
                                "& .StripeElement": {
                                    padding: "0px !important"
                                }
                            }}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            InputProps={{
                                inputComponent: StripeInput,
                                inputProps: {
                                    component: CardNumberElement,
                                    onChange: handleChange
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            label="Expiration date"
                            name="expdate"
                            variant="outlined"
                            sx={{
                                "& .StripeElement": {
                                    padding: "0px !important"
                                }
                            }}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            InputProps={{
                                inputComponent: StripeInput,
                                inputProps: {
                                    component: CardExpiryElement,
                                    onChange: handleChange
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            label="CVC"
                            name="cvcfield"
                            variant="outlined"
                            sx={{
                                "& .StripeElement": {
                                    padding: "0px !important"
                                }
                            }}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            InputProps={{
                                inputComponent: StripeInput,
                                inputProps: {
                                    component: CardCvcElement,
                                    onChange: handleChange
                                }
                            }}
                        />
                    </Grid>
                </Grid>
            )}
        </>
    );
};

const LogoContainer = () => {
    return (
        <Grid container alignItems="center" flexDirection="row">
            <Grid item xs={3}>
                <img
                    src={VISA_LOGO}
                    alt="visa"
                    style={{ width: "100%", height: "14px" }}
                />
            </Grid>
            <Grid item xs={3}>
                <img
                    src={MC_LOGO}
                    alt="mc"
                    style={{ width: "100%", height: "24px" }}
                />
            </Grid>
            <Grid item xs={3}>
                <img
                    src={AMEX_LOGO}
                    alt="amex"
                    style={{
                        width: "100%",
                        height: "29px",
                        position: "relative",
                        right: "2px"
                    }}
                />
            </Grid>
            <Grid item xs={3}>
                <img src={DISCOVER_LOGO} alt="disc" style={{ width: "100%" }} />
            </Grid>
        </Grid>
    );
};

export default StripeChargeCard;
